import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useHunt, useHuntFields, type HuntFilters } from '../api/queries'
import { useTenant } from '../api/tenant'
import type { Session } from '../api/types'
import { Failure } from '../components/Failure'
import { PinToDashboard } from '../components/PinToDashboard'
import { SaveAsRule } from '../components/SaveAsRule'
import { Suggested } from '../components/Suggested'

/**
 * Hunting over the event store.
 *
 * Three things this screen owes an analyst, all of them about not being misread.
 *
 * The window is always shown. A result set with no visible time range invites the
 * reading "there is nothing there", when what it means is "there is nothing there in
 * the last day" — different answers to a question about an intrusion.
 *
 * The cost is always shown. Somebody who cannot see that their last search read four
 * hundred million rows has no way to learn to write a narrower one.
 *
 * And a breakdown is offered next to the rows, because "who is this happening to" is
 * a count per value, and answering it by paging through a row list is how twenty
 * minutes go missing.
 */
export function HuntPage({ tenants = [], session }: { tenants?: string[]; session?: Session }) {
  const [tenant] = useTenant(tenants)
  // Seeded from the URL so a dashboard tile can be opened back up in the screen it
  // was written in. Read once: after that the form owns the state.
  const [params] = useSearchParams()
  const [draft, setDraft] = useState<HuntFilters>(() => ({
    limit: 200,
    q: params.get('q') ?? undefined,
    hostname: params.get('hostname') ?? undefined,
    user: params.get('user') ?? undefined,
    src_ip: params.get('src_ip') ?? undefined,
    group_by: params.get('group_by') ?? undefined,
  }))
  const [submitted, setSubmitted] = useState<HuntFilters | null>(null)
  const [saving, setSaving] = useState(false)
  const [pinning, setPinning] = useState(false)
  const query = useHunt(submitted ?? {}, submitted !== null)
  const fields = useHuntFields()

  function run(next: HuntFilters) {
    setDraft(next)
    setSubmitted({ ...next, tenant: tenant || undefined })
    // A new search invalidates a half-written rule or tile about the previous one.
    setSaving(false)
    setPinning(false)
  }

  const canSave = (session?.memberships ?? []).some(
    (membership) => membership.tenant === tenant && membership.can_administer,
  )
  const canPin = (session?.memberships ?? []).some(
    (membership) => membership.tenant === tenant && membership.can_triage,
  )

  return (
    <section aria-labelledby="hunt-heading">
      <h2 id="hunt-heading">Hunt</h2>
      <form
        className="panel"
        style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'end' }}
        onSubmit={(event) => {
          event.preventDefault()
          run(draft)
        }}
      >
        <label>
          <span className="muted">Text in the raw record</span>
          <input
            aria-label="Text in the raw record"
            placeholder="contains…"
            value={draft.q ?? ''}
            onChange={(event) => setDraft({ ...draft, q: event.target.value })}
          />
        </label>
        <Suggested
          label="Host"
          field="hostname"
          tenant={tenant}
          value={draft.hostname ?? ''}
          onChange={(value) => setDraft({ ...draft, hostname: value })}
        />
        <Suggested
          label="User"
          field="user"
          tenant={tenant}
          value={draft.user ?? ''}
          onChange={(value) => setDraft({ ...draft, user: value })}
        />
        <label>
          <span className="muted">From</span>
          <input
            aria-label="From"
            type="datetime-local"
            value={draft.since ?? ''}
            onChange={(event) => setDraft({ ...draft, since: event.target.value })}
          />
        </label>
        <label>
          <span className="muted">Break down by</span>
          <select
            aria-label="Break down by"
            value={draft.group_by ?? ''}
            onChange={(event) => setDraft({ ...draft, group_by: event.target.value || undefined })}
          >
            {/* Served by the API rather than remembered here: the list is a property
                of the storage schema, and a stale copy offers a field whose refusal
                then reads as a bug. */}
            <option value="">nothing — show the events</option>
            {(fields.data?.groupable ?? []).map((field) => (
              <option key={field} value={field}>
                {field}
              </option>
            ))}
          </select>
        </label>
        <button className="primary" type="submit">
          Search
        </button>
      </form>

      {query.isError && <Failure error={query.error} onRetry={() => query.refetch()} />}
      {query.isFetching && <p className="muted">Searching…</p>}

      {query.isSuccess && (
        <>
          <p className="muted">
            {query.data.group_by
              ? `${query.data.groups?.length ?? 0} value(s) of ${query.data.group_by}`
              : `${query.data.count ?? 0} event(s)`}{' '}
            between {new Date(query.data.from).toLocaleString()} and{' '}
            {new Date(query.data.to).toLocaleString()}
            {query.data.truncated &&
              (query.data.group_by
                ? ' — only the top values are shown, so these counts are not the whole picture'
                : ' — truncated at the row limit, narrow the search')}
            {' · '}
            {/* What it cost, in the analyst's own words. */}
            answered in {query.data.took_ms ?? '?'} ms
            {typeof query.data.scanned_rows === 'number' &&
              `, ${query.data.scanned_rows.toLocaleString()} rows read`}
          </p>

          {/* Offered only once a search has actually returned. A rule saved from a
              query nobody has run is a rule nobody has seen the output of. */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
            {!query.data.group_by && !saving && (
              <button onClick={() => setSaving(true)}>Save this search as a rule</button>
            )}
            {!pinning && (
              <button onClick={() => setPinning(true)}>Pin this search to the dashboard</button>
            )}
          </div>
          {saving && submitted && (
            <SaveAsRule filters={submitted} canSave={canSave} onClose={() => setSaving(false)} />
          )}
          {pinning && submitted && (
            <PinToDashboard
              filters={submitted}
              groupBy={query.data.group_by ?? ''}
              canPin={canPin}
              onDone={() => setPinning(false)}
            />
          )}

          {query.data.group_by ? (
            <Breakdown
              field={query.data.group_by}
              groups={query.data.groups ?? []}
              onPivot={(value) => run({ ...draft, ...pivot(query.data.group_by ?? '', value) })}
            />
          ) : (
            <div className="panel" style={{ maxHeight: '60vh', overflow: 'auto' }}>
              {(query.data.results ?? []).length === 0 && (
                <p className="empty">
                  Nothing matched in this window. That is an answer about this window, not
                  about the estate.
                </p>
              )}
              {(query.data.results ?? []).map((row, index) => (
                <p key={String(row.event_id ?? index)} className="mono" style={{ margin: 0 }}>
                  {String(row.time ?? '')} {String(row.hostname ?? '')} {String(row.raw_data ?? '')}
                </p>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  )
}

/** The breakdown fields the console can turn back into a filter. */
function pivot(field: string, value: string): HuntFilters {
  if (field === 'hostname') return { hostname: value, group_by: undefined }
  if (field === 'user') return { user: value, group_by: undefined }
  if (field === 'src_ip') return { src_ip: value, group_by: undefined }
  // Everything else has no filter to become. Rather than silently doing nothing, the
  // button is not offered — see `Breakdown`.
  return {}
}

const PIVOTABLE = new Set(['hostname', 'user', 'src_ip'])

/**
 * Counts per value, as a table with a proportional bar.
 *
 * A table and not a chart: the numbers are the point, they are read exactly, and a
 * bar that cannot be read as a number is decoration. The bar is there because
 * relative size is genuinely faster to see than to compute.
 */
function Breakdown({
  field,
  groups,
  onPivot,
}: {
  field: string
  groups: { value: string; count: number }[]
  onPivot: (value: string) => void
}) {
  const peak = Math.max(1, ...groups.map((group) => group.count))
  if (groups.length === 0) {
    return <p className="empty">No values of {field} occur in this window.</p>
  }

  return (
    <table className="panel" style={{ width: '100%', borderCollapse: 'collapse' }}>
      <caption className="muted" style={{ captionSide: 'bottom', textAlign: 'left' }}>
        Counted over the same filters and window as the search above.
      </caption>
      <thead>
        <tr style={{ textAlign: 'left' }}>
          <th scope="col">{field}</th>
          <th scope="col">events</th>
          <th scope="col">
            <span className="visually-hidden">share of the total</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => (
          <tr key={group.value}>
            <td className="mono">
              {PIVOTABLE.has(field) ? (
                <button type="button" className="linklike" onClick={() => onPivot(group.value)}>
                  {group.value}
                </button>
              ) : (
                group.value
              )}
            </td>
            <td className="mono">{group.count.toLocaleString()}</td>
            <td style={{ width: '40%' }}>
              {/* Presentation only; the number beside it is the accessible value. */}
              <div
                aria-hidden="true"
                style={{
                  height: 8,
                  width: `${Math.round((group.count / peak) * 100)}%`,
                  background: 'var(--accent)',
                  borderRadius: 2,
                }}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
